import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HeaderBar } from '../components/HeaderBar';
import { ProgressStepper } from '../components/ProgressStepper';
import { ActionButton } from '../components/ActionButton';
export const ProposalSubmissionScreen: React.FC = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const acceptedFileTypes = ['.pdf', '.docx', '.xlsx', '.pptx', '.csv'];
  const validateFile = (file: File): boolean => {
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!acceptedFileTypes.includes(fileExtension)) {
      setError(`Invalid file type. Please upload one of the following: ${acceptedFileTypes.join(', ')}`);
      return false;
    }
    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size exceeds 10MB limit. Please upload a smaller file.');
      return false;
    }
    setError(null);
    return true;
  };
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFile = e.dataTransfer.files[0];
      if (validateFile(droppedFile)) {
        setFile(droppedFile);
      }
    }
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      if (validateFile(selectedFile)) {
        setFile(selectedFile);
      }
    }
  };
  const handleSubmit = () => {
    if (!file) return;
    setIsSubmitting(true);
    // Simulate file processing delay
    setTimeout(() => {
      setIsSubmitting(false);
      navigate('/verify-data');
    }, 2000);
  };
  return <div className="min-h-screen bg-[#f5f6f8]" data-id="element-426">
      <HeaderBar logoSrc="https://uploadthingy.s3.us-west-1.amazonaws.com/tXtrQ38XxwUBTwmzoNN8ZL/Prospeum_Logo_White_SVG.svg" logoAlt="Prospeum Logo" data-id="element-427" />
      <main className="container mx-auto px-4 py-8" data-id="element-428">
        <div className="mb-8" data-id="element-429">
          <h1 className="text-2xl font-bold text-[#333333] mb-6" data-id="element-430">
            Business Laptop - Proposal Submission
          </h1>
          <ProgressStepper steps={['Submit Proposal', 'Verify Details', 'Submission Complete']} currentStep={0} data-id="element-431" />
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-3xl mx-auto" data-id="element-432">
          <h2 className="text-xl font-bold text-[#333333] mb-6 text-center" data-id="element-433">
            Submit Your Proposal
          </h2>
          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6" data-id="element-434">
              {error}
            </div>}
          <div className={`border-2 border-dashed rounded-lg p-8 text-center ${isDragging ? 'border-[#005a9e] bg-[#f0f7ff]' : 'border-gray-300'} ${file ? 'bg-[#f0f7ff] border-[#005a9e]' : ''}`} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} data-id="element-435">
            {!file ? <>
                <div className="mb-4" data-id="element-436">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-437">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" data-id="element-438" />
                  </svg>
                </div>
                <p className="text-[#333333] mb-2" data-id="element-439">
                  Drag and drop your proposal file here
                </p>
                <p className="text-gray-500 text-sm mb-4" data-id="element-440">
                  Accepted file types: {acceptedFileTypes.join(', ')}
                </p>
                <div data-id="element-441">
                  <label htmlFor="file-upload" className="cursor-pointer text-[#005a9e] hover:underline" data-id="element-442">
                    Or click to browse files
                    <input id="file-upload" type="file" className="hidden" accept={acceptedFileTypes.join(',')} onChange={handleFileChange} data-id="element-443" />
                  </label>
                </div>
              </> : <div className="flex flex-col items-center" data-id="element-444">
                <svg className="h-12 w-12 text-[#4caf50] mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-445">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" data-id="element-446" />
                </svg>
                <p className="text-[#333333] font-medium mb-1" data-id="element-447">
                  File ready for submission
                </p>
                <p className="text-[#333333]" data-id="element-448">{file.name}</p>
                <p className="text-gray-500 text-sm mt-1" data-id="element-449">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </p>
                <button className="mt-4 text-red-500 text-sm hover:underline" onClick={() => setFile(null)} data-id="element-450">
                  Remove file
                </button>
              </div>}
          </div>
          <div className="flex flex-col sm:flex-row sm:justify-between items-center mt-8 space-y-4 sm:space-y-0" data-id="element-451">
            <button onClick={() => navigate('/')} className="text-[#005a9e] hover:underline text-sm" data-id="element-452">
              ← Back to Request Details
            </button>
            <ActionButton label={isSubmitting ? 'Analyzing...' : 'Submit for Analysis'} onClick={handleSubmit} disabled={!file || isSubmitting} className="w-full sm:w-auto" icon={isSubmitting ? <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-454">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-455"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-456"></path>
                  </svg> : undefined} data-id="element-453" />
          </div>
        </div>
      </main>
    </div>;
};